/**
 * FT-116: Fleet-Wide Risk Assessments Hook
 *
 * Stores risk assessments at organization level (not per-vessel).
 * Uses noScope: true to bypass vessel ID suffixing.
 *
 * Key: 'fleet_risk_assessments' (org-scoped, syncs to sr.fleet_risk_assessments.v1)
 *
 * Pattern mirrors FT-05 useFleetCrew.js for consistency.
 * This is PARALLEL to useRiskAssessmentsData — existing vessel-scoped RAs are untouched.
 */

import React, { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { usePermissions } from '../../usePermissions';
import { useVessel } from '../../../contexts/VesselContext';
import { devLog } from '../../../utils/devLog';
import { v4 as uuidv4 } from 'uuid';
import { getOrgId } from '../../../utils/getOrgId';  // FT-174: Stamp organizationId on records

// RBAC helper
const canDo = (can, resource, action) => {
  if (!can) return false;
  try {
    return can(action, resource);
  } catch {
    return false;
  }
};

// Calculate risk matrix value (matching useRiskAssessmentsData)
const calculateRisk = (likelihood, severity) => {
  const matrix = {
    1: { 1: 'low', 2: 'low', 3: 'medium', 4: 'medium', 5: 'high' },
    2: { 1: 'low', 2: 'low', 3: 'medium', 4: 'high', 5: 'high' },
    3: { 1: 'low', 2: 'medium', 3: 'high', 4: 'high', 5: 'extreme' },
    4: { 1: 'medium', 2: 'high', 3: 'high', 4: 'extreme', 5: 'extreme' },
    5: { 1: 'high', 2: 'high', 3: 'extreme', 4: 'extreme', 5: 'extreme' }
  };
  return matrix[likelihood]?.[severity] || 'unknown';
};

// FT-174: Normalize RA input (matching useRiskAssessmentsData structure + fleet fields)
// Always stamp organizationId to prevent cross-org contamination
const normalizeRAInput = (input) => {
  const now = new Date().toISOString();
  const currentOrgId = getOrgId();
  return {
    id: input.id || uuidv4(),
    title: input.title ?? '',
    type: input.type ?? 'operational', // operational, navigational, machinery, cargo, environmental
    category: input.category ?? 'routine', // routine, non_routine, emergency
    status: input.status ?? 'draft', // draft, active, under_review, archived
    version: input.version ?? '1.0',

    // Risk details
    task: input.task ?? '',
    location: input.location ?? '',
    department: input.department ?? '',
    frequency: input.frequency ?? 'daily', // daily, weekly, monthly, yearly, as_required

    // Hazards and risks
    hazards: input.hazards ?? [],

    // Control measures
    existingControls: input.existingControls ?? [],
    additionalControls: input.additionalControls ?? [],
    ppe: input.ppe ?? [],

    // Review and approval
    preparedBy: input.preparedBy ?? '',
    preparedDate: input.preparedDate ?? now,
    reviewedBy: input.reviewedBy ?? '',
    reviewedDate: input.reviewedDate ?? null,
    approvedBy: input.approvedBy ?? '',
    approvedDate: input.approvedDate ?? null,
    nextReviewDate: input.nextReviewDate ?? null,
    reviewStatus: input.reviewStatus ?? null,
    reviewComments: input.reviewComments ?? null,
    reviewHistory: input.reviewHistory ?? [],

    // Metadata
    attachments: input.attachments ?? [],
    references: input.references ?? [],
    notes: input.notes ?? '',
    createdAt: input.createdAt ?? now,
    updatedAt: now,
    createdBy: input.createdBy ?? null,
    updatedBy: input.updatedBy ?? null,

    // FT-116: Fleet-specific fields (mirrors FT-05 crew pattern)
    assignedVessels: input.assignedVessels ?? [],  // Array of vessel IDs this RA applies to
    homeVessel: input.homeVessel ?? null,          // Primary vessel where RA was created

    // FT-174: Organization stamp (prevents cross-org contamination)
    organizationId: input.organizationId || currentOrgId
  };
};

// FT-174: Normalize RA record
// Preserve organizationId if present (don't overwrite with current org on read)
const normalizeRARecord = (record) => {
  if (!record || typeof record !== 'object') return null;
  const ra = {
    ...record,
    id: record.id || uuidv4(),
    title: record.title ?? '',
    type: record.type ?? 'operational',
    category: record.category ?? 'routine',
    status: record.status ?? 'draft',
    hazards: record.hazards ?? [],
    assignedVessels: record.assignedVessels ?? [],
    homeVessel: record.homeVessel ?? null,
    // FT-174: Preserve organizationId (don't overwrite with current org on read)
    organizationId: record.organizationId
  };
  // FT-498: Recalculate aggregate scores from hazards on every load (fixes stale 0 values)
  if (ra.hazards && ra.hazards.length > 0) {
    ra.initialRiskScore = Math.max(...ra.hazards.map(h => (h.initialLikelihood || 1) * (h.initialSeverity || 1)));
    ra.residualRiskScore = Math.max(...ra.hazards.map(h => (h.residualLikelihood || h.initialLikelihood || 1) * (h.residualSeverity || h.initialSeverity || 1)));
    // FT-498b: also derive riskLevel string — Risk Summary panel counts by this field, not by
    // the numeric score. Must match riskCalculationService.getRiskLevel thresholds exactly.
    ra.riskLevel = ra.residualRiskScore >= 20 ? 'extreme'
                 : ra.residualRiskScore >= 12 ? 'high'
                 : ra.residualRiskScore >= 6  ? 'medium'
                 : 'low';
  }
  return ra;
};

// Filter out wrapper records (sync artifacts)
const isWrapperRecord = (item) => {
  if (!item || typeof item !== 'object') return true;
  if (item.data && item.data._isArray) return true;
  if (item.data && Array.isArray(item.data.items)) return true;
  if (item.id && typeof item.id === 'string' && /^[0-9a-f-]{36}_/.test(item.id)) return true;
  return false;
};

// Normalize legacy data
const normalizeLegacy = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.filter(r => !isWrapperRecord(r)).map(normalizeRARecord).filter(Boolean);
};

/**
 * useFleetRiskAssessments - Organization-wide risk assessments
 *
 * FT-116: Uses noScope: true to store at org level
 * Same interface pattern as useFleetCrew for consistency
 */
export const useFleetRiskAssessments = () => {
  // Version marker for dev tracking
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      devLog('[useFleetRiskAssessments] v2026-03-30 - FT-116 Fleet-wide risk assessments (org-scoped)');
    }
  }, []);

  // FT-116: noScope: true = org-scoped storage (no vessel suffix)
  const { value: raw = [], setValue } = useStorageState('fleet_risk_assessments', [], { noScope: true });
  const { can, currentUser } = usePermissions();
  const userName = currentUser?.name || 'Unknown User';

  // FT-116: Get currentVesselId for pre-memoized vesselRAs
  const { currentVesselId } = useVessel();

  // FT-175: Normalize on read and filter out tombstoned records
  // Records with deletedAt are soft-deleted (tombstones for sync)
  const list = useMemo(() => {
    const normalized = normalizeLegacy(raw);
    return normalized.filter(ra => !ra.deletedAt);
  }, [raw]);

  // FT-175: Get all records including deleted (for admin/restore purposes)
  const listWithDeleted = useMemo(() => normalizeLegacy(raw), [raw]);

  // FT-116: Pre-memoized vessel RAs — stable reference, prevents infinite loops in consumers
  // This is the PRIMARY data that most consumers will use
  const vesselRiskAssessments = useMemo(() => {
    if (!currentVesselId) return [];
    return list.filter(ra =>
      ra.status !== 'archived' &&
      (ra.homeVessel === currentVesselId || (ra.assignedVessels || []).includes(currentVesselId))
    );
  }, [list, currentVesselId]);

  // Add new risk assessment - RBAC-gated
  const add = useCallback((input = {}) => {
    if (!canDo(can, 'risk_assessments', 'create')) {
      devLog('[useFleetRiskAssessments] Permission denied: create');
      return null;
    }

    const ra = normalizeRAInput({
      ...input,
      createdBy: userName,
      // FT-116: Auto-set homeVessel to current vessel if not specified
      homeVessel: input.homeVessel ?? currentVesselId,
      // FT-116: Auto-assign to current vessel if no vessels specified
      assignedVessels: input.assignedVessels?.length > 0
        ? input.assignedVessels
        : currentVesselId ? [currentVesselId] : []
    });

    // Auto-calculate risk scores if hazards present
    // NOTE: wizard hazards use initialLikelihood/initialSeverity (Hazard model fields),
    // not the legacy likelihood/severity field names.
    if (ra.hazards?.length > 0) {
      ra.hazards = ra.hazards.map(hazard => {
        // Support both field naming conventions
        const initL = hazard.initialLikelihood || hazard.likelihood || 1;
        const initS = hazard.initialSeverity || hazard.severity || 1;
        const residL = hazard.residualLikelihood || hazard.likelihood || initL;
        const residS = hazard.residualSeverity || hazard.severity || initS;
        return {
          ...hazard,
          initialRiskScore: initL * initS,
          residualRiskScore: residL * residS,
          initialRisk: calculateRisk(initL, initS),
          residualRisk: calculateRisk(residL, residS)
        };
      });
      // Aggregate to RA level: highest hazard score wins (same logic as Hazard model)
      ra.initialRiskScore = Math.max(...ra.hazards.map(h => h.initialRiskScore || 0));
      ra.residualRiskScore = Math.max(...ra.hazards.map(h => h.residualRiskScore || 0));
    }

    if (process.env.NODE_ENV !== 'production') {
      devLog('[useFleetRiskAssessments] Adding RA:', ra.title);
    }

    setValue(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return [...current, ra];
    });

    return ra;
  }, [can, setValue, userName, currentVesselId]);

  // Update existing risk assessment - RBAC-gated
  const update = useCallback((id, updates) => {
    if (!canDo(can, 'risk_assessments', 'update')) {
      devLog('[useFleetRiskAssessments] Permission denied: update');
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(ra => {
        if (ra.id !== id) return ra;

        const updated = { ...ra, ...updates, updatedAt: now, updatedBy: userName };

        // Recalculate risk scores if hazards updated
        // NOTE: wizard hazards use initialLikelihood/initialSeverity (Hazard model fields),
        // not the legacy likelihood/severity field names.
        if (updates.hazards) {
          updated.hazards = updates.hazards.map(hazard => {
            const initL = hazard.initialLikelihood || hazard.likelihood || 1;
            const initS = hazard.initialSeverity || hazard.severity || 1;
            const residL = hazard.residualLikelihood || hazard.likelihood || initL;
            const residS = hazard.residualSeverity || hazard.severity || initS;
            return {
              ...hazard,
              initialRiskScore: initL * initS,
              residualRiskScore: residL * residS,
              initialRisk: calculateRisk(initL, initS),
              residualRisk: calculateRisk(residL, residS)
            };
          });
          // Aggregate to RA level: highest hazard score wins
          updated.initialRiskScore = Math.max(...updated.hazards.map(h => h.initialRiskScore || 0));
          updated.residualRiskScore = Math.max(...updated.hazards.map(h => h.residualRiskScore || 0));
        }

        return updated;
      });
    });

    return true;
  }, [can, setValue, userName]);

  // Archive risk assessment (soft delete) - RBAC-gated
  const archive = useCallback((id) => {
    if (!canDo(can, 'risk_assessments', 'delete')) {
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(ra => ra.id === id
        ? { ...ra, status: 'archived', updatedAt: now, updatedBy: userName }
        : ra
      );
    });

    return true;
  }, [can, setValue, userName]);

  // FT-175: Restore risk assessment - clears tombstone fields AND sets status to active
  // Can restore both archived (status=archived) and deleted (deletedAt set) records
  const restore = useCallback((id) => {
    if (!canDo(can, 'risk_assessments', 'update')) {
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(ra => ra.id === id
        ? {
            ...ra,
            status: 'active',
            // FT-175: Clear tombstone fields on restore
            deletedAt: null,
            deletedBy: null,
            _hardDelete: false,
            updatedAt: now,
            updatedBy: userName
          }
        : ra
      );
    });

    devLog(`[useFleetRiskAssessments] FT-175: Restored RA ${id}`);
    return true;
  }, [can, setValue, userName]);

  // FT-175: Delete risk assessment - now uses tombstone pattern
  // Records are marked with deletedAt instead of being removed from array
  // This allows sync to propagate deletions properly
  // @param id - ID of the RA to delete
  // @param hardDelete - if true, marks for permanent deletion (no restore)
  const remove = useCallback((id, hardDelete = false) => {
    if (!canDo(can, 'risk_assessments', 'delete')) {
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(ra => ra.id === id
        ? {
            ...ra,
            deletedAt: now,
            deletedBy: userName,
            _hardDelete: hardDelete,  // Flag for future cloud cleanup
            updatedAt: now,
            updatedBy: userName
          }
        : ra
      );
    });

    devLog(`[useFleetRiskAssessments] FT-175: Tombstoned RA ${id} (hardDelete: ${hardDelete})`);
    return true;
  }, [can, setValue, userName]);

  // Get single RA by ID
  const getById = useCallback((id) => {
    return list.find(ra => ra.id === id) || null;
  }, [list]);

  // Get active RAs only
  const getActive = useCallback(() => {
    return list.filter(ra => ra.status === 'active');
  }, [list]);

  // Get archived RAs only
  const getArchived = useCallback(() => {
    return list.filter(ra => ra.status === 'archived');
  }, [list]);

  // Get RAs due for review
  const getDueForReview = useCallback(() => {
    const today = new Date();
    return list
      .filter(ra => {
        if (ra.status !== 'active' || !ra.nextReviewDate) return false;
        const reviewDate = new Date(ra.nextReviewDate);
        return reviewDate <= today;
      })
      .sort((a, b) => new Date(a.nextReviewDate) - new Date(b.nextReviewDate));
  }, [list]);

  // FT-116: Get RAs assigned to a specific vessel
  const getByVessel = useCallback((vesselId) => {
    if (!vesselId) return [];
    return list.filter(ra =>
      ra.status !== 'archived' &&
      (ra.homeVessel === vesselId || (ra.assignedVessels || []).includes(vesselId))
    );
  }, [list]);

  // FT-116: Assign RA to vessel
  const assignToVessel = useCallback((raId, vesselId) => {
    if (!canDo(can, 'risk_assessments', 'update')) {
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(ra => {
        if (ra.id !== raId) return ra;
        const vessels = new Set(ra.assignedVessels || []);
        vessels.add(vesselId);
        return {
          ...ra,
          assignedVessels: Array.from(vessels),
          updatedAt: now,
          updatedBy: userName
        };
      });
    });

    return true;
  }, [can, setValue, userName]);

  // FT-116: Remove RA from vessel
  const removeFromVessel = useCallback((raId, vesselId) => {
    if (!canDo(can, 'risk_assessments', 'update')) {
      return false;
    }

    const now = new Date().toISOString();

    setValue(prev => {
      const current = normalizeLegacy(prev);
      return current.map(ra => {
        if (ra.id !== raId) return ra;
        const vessels = (ra.assignedVessels || []).filter(v => v !== vesselId);
        return {
          ...ra,
          assignedVessels: vessels,
          homeVessel: ra.homeVessel === vesselId ? null : ra.homeVessel,
          updatedAt: now,
          updatedBy: userName
        };
      });
    });

    return true;
  }, [can, setValue, userName]);

  // FT-116: Get RAs NOT assigned to current vessel (for "Add from Org" picker)
  const getUnassignedToCurrentVessel = useCallback(() => {
    if (!currentVesselId) return [];
    return list.filter(ra => {
      if (ra.status === 'archived') return false;
      const isHomeVessel = ra.homeVessel === currentVesselId;
      const isAssigned = (ra.assignedVessels || []).includes(currentVesselId);
      return !isHomeVessel && !isAssigned;
    });
  }, [list, currentVesselId]);

  // Approve risk assessment
  const approveRA = useCallback((id, comments = '') => {
    if (!canDo(can, 'risk_assessments', 'update')) {
      return false;
    }

    const now = new Date().toISOString();
    const ra = getById(id);
    if (!ra) return false;

    // Calculate next review date (annual by default)
    const nextReview = new Date();
    nextReview.setFullYear(nextReview.getFullYear() + 1);

    return update(id, {
      reviewedBy: userName,
      reviewedDate: now,
      reviewStatus: 'approved',
      reviewComments: comments,
      approvedBy: userName,
      approvedDate: now,
      status: 'active',
      nextReviewDate: nextReview.toISOString(),
      reviewHistory: [
        ...(ra.reviewHistory || []),
        {
          date: now,
          reviewer: userName,
          action: 'approved',
          comments
        }
      ]
    });
  }, [can, getById, update, userName]);

  // Reject risk assessment
  const rejectRA = useCallback((id, reason = '') => {
    if (!canDo(can, 'risk_assessments', 'update')) {
      return false;
    }

    const now = new Date().toISOString();
    const ra = getById(id);
    if (!ra) return false;

    return update(id, {
      reviewedBy: userName,
      reviewedDate: now,
      reviewStatus: 'rejected',
      reviewComments: reason,
      status: 'draft',
      reviewHistory: [
        ...(ra.reviewHistory || []),
        {
          date: now,
          reviewer: userName,
          action: 'rejected',
          comments: reason
        }
      ]
    });
  }, [can, getById, update, userName]);

  // Get statistics
  const getStats = useCallback(() => {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    return {
      total: list.length,
      active: list.filter(ra => ra.status === 'active').length,
      draft: list.filter(ra => ra.status === 'draft').length,
      underReview: list.filter(ra => ra.status === 'under_review').length,
      archived: list.filter(ra => ra.status === 'archived').length,
      dueForReview: getDueForReview().length,
      dueSoon: list.filter(ra => {
        if (ra.status !== 'active' || !ra.nextReviewDate) return false;
        const reviewDate = new Date(ra.nextReviewDate);
        return reviewDate > today && reviewDate <= thirtyDaysFromNow;
      }).length,
      highRiskCount: list.filter(ra =>
        ra.hazards?.some(h => h.residualRisk === 'high' || h.residualRisk === 'extreme')
      ).length,
      // FT-116: Fleet stats
      vesselCount: vesselRiskAssessments.length,
      unassignedCount: getUnassignedToCurrentVessel().length
    };
  }, [list, getDueForReview, vesselRiskAssessments, getUnassignedToCurrentVessel]);

  // FT-175: Get deleted (tombstoned) records for admin/restore view
  const getDeleted = useCallback(() => {
    return listWithDeleted.filter(ra => ra.deletedAt);
  }, [listWithDeleted]);

  // Memoized return
  return useMemo(() => ({
    // Data
    list,                        // All org RAs (excludes tombstoned)
    listWithDeleted,             // FT-175: All RAs including tombstoned (for admin)
    vesselRiskAssessments,       // FT-116: Pre-memoized RAs for current vessel

    // CRUD operations (RBAC-gated)
    add,
    update,
    archive,
    restore,
    remove,

    // Query helpers
    getById,
    getActive,
    getArchived,
    getDeleted,              // FT-175: Get tombstoned records
    getDueForReview,
    getStats,

    // Approval flow
    approveRA,
    rejectRA,

    // FT-116: Fleet-specific operations
    getByVessel,
    assignToVessel,
    removeFromVessel,
    getUnassignedToCurrentVessel,

    // FT-116: Computed values
    totalRAs: list.length,
    activeRAs: list.filter(ra => ra.status !== 'archived').length,
    archivedRAs: list.filter(ra => ra.status === 'archived').length,
    vesselRACount: vesselRiskAssessments.length,

    // FT-116: Export helpers for consumers
    currentVesselId,
    calculateRisk,

    // Permission helpers
    canEdit: () => canDo(can, 'risk_assessments', 'update'),
    canDelete: () => canDo(can, 'risk_assessments', 'delete'),
    canCreate: () => canDo(can, 'risk_assessments', 'create')
  }), [
    list, listWithDeleted, vesselRiskAssessments, currentVesselId, can,
    add, update, archive, restore, remove,
    getById, getActive, getArchived, getDeleted, getDueForReview, getStats,
    approveRA, rejectRA,
    getByVessel, assignToVessel, removeFromVessel, getUnassignedToCurrentVessel
  ]);
};

export default useFleetRiskAssessments;
